import React, { useCallback, useEffect, useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import {
  CallSplitOutlined as SplitIcon,
  LocationOnOutlined as ZoneIcon,
  SortOutlined as FifoIcon,
  SwapVertOutlined as PriorityIcon,
  WarehouseOutlined as WarehouseIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import DashboardContainer from "../../components/dashboard-container";
import DeleteConfirmationModal from "../../components/deleteConfirmationModal";
import CustomPagination from "../../components/pagination";
import { fulfillmentRuleService } from "../../utils/services/fulfillmentRuleService";
import { clientService } from "../../utils/services/clientService";
import { wmsService } from "../../utils/services/wmsProvider";
import AdminView from "./components/AdminView";
import ClientView, { INFO_ITEMS } from "./components/ClientView";
import RuleBuilderDialog from "./components/RuleBuilderDialog";
import { LOT_STRATEGIES, WAREHOUSE_STRATEGIES } from "./constants";

// ─── Column filter config ──────────────────────────────────────────────────────

const INITIAL_FILTER_COLUMNS = [
  {
    key: "name",
    filter: [],
    sort: 0,
    options: [],
    displayKey: "label",
    id: "value",
    loadMoreButton: false,
    fetchApi: null,
    searchKey: "",
  },
  {
    key: "client",
    filter: [],
    sort: 0,
    options: [],
    displayKey: "business_name",
    id: "_id",
    loadMoreButton: true,
    fetchApi: async (searchValue, page, limit) => {
      const res = await clientService.fetch(
        limit || 15,
        (page - 1) * (limit || 15),
        searchValue,
      );
      return res?.data || [];
    },
    searchKey: "name",
  },
  {
    key: "lot_strategy",
    filter: [],
    sort: 0,
    options: LOT_STRATEGIES.map((o) => ({ label: o.label, value: o.value })),
    displayKey: "label",
    id: "value",
    loadMoreButton: false,
    fetchApi: null,
    searchKey: "",
  },
  {
    key: "warehouse_strategy",
    filter: [],
    sort: 0,
    options: WAREHOUSE_STRATEGIES.map((o) => ({
      label: o.label,
      value: o.value,
    })),
    displayKey: "label",
    id: "value",
    loadMoreButton: false,
    fetchApi: null,
    searchKey: "",
  },
  {
    key: "is_active",
    filter: [],
    sort: 0,
    options: [
      { label: "Active", value: "true" },
      { label: "Inactive", value: "false" },
    ],
    displayKey: "label",
    id: "value",
    loadMoreButton: false,
    fetchApi: null,
    searchKey: "",
  },
];

// ─── Main Page ─────────────────────────────────────────────────────────────────

const FulfillmentRule = () => {
  const parsedUser = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = parsedUser?.role === "admin";
  const clientId = localStorage.getItem("client_id");

  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [clients, setClients] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [dataCount, setDataCount] = useState(0);
  const [filterData, setFilterData] = useState({
    columns: INITIAL_FILTER_COLUMNS,
  });

  const buildFilterQuery = useCallback(() => {
    let q = "";
    filterData.columns.forEach((col) => {
      if (col.filter?.length) {
        if (col.key === "client") {
          col.filter.forEach((f, i) => {
            q += `&client[$in][${i}]=${f._id || f}`;
          });
        } else if (col.key === "is_active") {
          q += `&is_active=${col.filter[0]?.value ?? col.filter[0]}`;
        } else if (col.key === "lot_strategy") {
          col.filter.forEach((f, i) => {
            q += `&lot_strategy[$in][${i}]=${f.value ?? f}`;
          });
        } else if (col.key === "warehouse_strategy") {
          col.filter.forEach((f, i) => {
            q += `&warehouse_strategy[$in][${i}]=${f.value ?? f}`;
          });
        }
      }
      if (col.sort && col.key === "name") {
        q += `&$sort[name]=${col.sort}`;
      }
    });
    return q;
  }, [filterData]);

  const handleChangeFilter = ({ key, filter, sort, searchKey }) => {
    setFilterData((prev) => ({
      columns: prev.columns.map((c) =>
        c.key === key ? { ...c, filter, sort, searchKey } : c,
      ),
    }));
    setPage(0);
  };

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const filterQuery = buildFilterQuery();
      const filterClient = isAdmin
        ? filterData.columns.find((c) => c.key === "client")?.filter?.[0]
            ?._id || null
        : clientId;
      const res = await fulfillmentRuleService.fetch(
        rowsPerPage,
        rowsPerPage * page,
        filterClient,
        searchValue,
        filterQuery,
      );
      setRules(res?.data || []);
      setDataCount(res?.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [
    isAdmin,
    clientId,
    page,
    rowsPerPage,
    searchValue,
    buildFilterQuery,
    filterData,
  ]);

  const fetchWarehouses = useCallback(async () => {
    try {
      const res = await wmsService.fetch(isAdmin ? null : clientId, 100, 0);
      setWarehouses(res?.data || []);
    } catch (e) {
      console.error(e);
    }
  }, [isAdmin, clientId]);

  const fetchClients = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await clientService.fetch(100, 0);
      setClients(res?.data || []);
    } catch (e) {
      console.error(e);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);
  useEffect(() => {
    fetchWarehouses();
    fetchClients();
  }, [fetchWarehouses, fetchClients]);

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setDialogOpen(true);
  };
  const handleAdd = () => {
    setEditingRule(null);
    setDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await fulfillmentRuleService.delete(deleteTarget._id);
      toast.success("Rule deleted");
      fetchRules();
    } catch (e) {
      console.error(e);
    } finally {
      setDeleteTarget(null);
    }
  };

  if (isAdmin) {
    return (
      <>
        <DashboardContainer
          header="Fulfillment Rules"
          title="+ Rule"
          onClickAddButton={handleAdd}
          hideUploadButton
          onSearchData={(val) => {
            setSearchValue(val.trim());
            setPage(0);
          }}
          hideFilterClient
          onClickRefresh={() => fetchRules()}
        >
          <AdminView
            rules={rules}
            loading={loading}
            onEdit={handleEdit}
            onDelete={(r) => setDeleteTarget(r)}
            currentPage={page}
            itemsPerPage={rowsPerPage}
            filterData={filterData}
            onChangeFilter={handleChangeFilter}
          />
          <CustomPagination
            dataCount={dataCount}
            rowsPerPage={rowsPerPage}
            page={page}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
            disableNext={dataCount <= (page + 1) * rowsPerPage}
          />
        </DashboardContainer>

        <RuleBuilderDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          editingRule={editingRule}
          onSaved={fetchRules}
          warehouses={warehouses}
          clients={clients}
          isAdmin={isAdmin}
          clientId={clientId}
        />

        <DeleteConfirmationModal
          open={!!deleteTarget}
          handleClose={() => setDeleteTarget(null)}
          handleDelete={handleDeleteConfirm}
          title={`Are you sure you want to delete "${deleteTarget?.name}"?`}
          deleteButtonTitle="Delete"
        />
      </>
    );
  }

  // ── Client View ──
  return (
    <>
      <Box sx={{ minHeight: "100vh", background: "var(--dashboard-bg)", fontFamily: "Poppins, sans-serif" }}>
        {/* Page Header */}
        <Box
          sx={{
            px: { xs: 2, sm: 4 },
            pt: 3,
            pb: 2,
            borderBottom: "1px solid var(--border-color)",
            background: "var(--bg-paper)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2.5,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <WarehouseIcon sx={{ color: "#fff", fontSize: 24 }} />
            </Box>
            <Box>
              <Typography fontFamily="Poppins, sans-serif" fontSize={20} fontWeight={700} color="var(--text-primary)">
                Fulfillment Configuration
              </Typography>
              <Typography fontFamily="Poppins, sans-serif" fontSize={12} color="var(--text-secondary)">
                Manage how your orders are allocated across warehouses during processing
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={1.5} sx={{ mt: 2 }}>
            {INFO_ITEMS.map((item) => {
              const Icon = item.icon;
              return (
                <Grid item xs={6} sm={3} key={item.label}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 1,
                      borderRadius: 2,
                      background: "var(--bg-primary)",
                    }}
                  >
                    <Icon sx={{ fontSize: 18, color: item.color }} />
                    <Box>
                      <Typography fontSize={11} fontWeight={600} color="var(--text-primary)" fontFamily="Poppins, sans-serif">
                        {item.label}
                      </Typography>
                      <Typography fontSize={10} color="var(--text-secondary)" fontFamily="Poppins, sans-serif">
                        {item.desc}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        <ClientView rules={rules} loading={loading} onEdit={handleEdit} onAdd={handleAdd} />
      </Box>

      <RuleBuilderDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        editingRule={editingRule}
        onSaved={fetchRules}
        warehouses={warehouses}
        clients={[]}
        isAdmin={false}
        clientId={clientId}
      />
    </>
  );
};

export default FulfillmentRule;
